import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { Schemas } from '@/api/client';
import { organizationsListQuery } from '@/api/queries';
import { paths } from '@/app/paths';
import { Button, CapsLabel, FieldHint, Modal, ModalFooter, ModalHeader, OptionCard, Select, useModalTitleId, useToast } from '@/design-system';
import { shortName } from '@/lib/format';
import { createWebsite } from './api';
import s from './sites.module.css';

type Props = { organizationId?: string; onClose: () => void };

/** «Создать сайт» (DESIGN_SPEC §4.12): organization, type, mode → the site is created and its Structure tab opens. */
export function CreateSiteModal({ organizationId, onClose }: Props) {
  const { t } = useTranslation('sites');
  const { t: tc } = useTranslation('common');
  const titleId = useModalTitleId();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const orgs = useQuery(organizationsListQuery).data ?? [];
  const active = orgs.filter((o) => o.status !== 'archived');
  const [orgId, setOrgId] = useState<string | undefined>(organizationId);
  const [type, setType] = useState<Schemas['WebsiteType']>('ecommerce');
  const [mode, setMode] = useState<Schemas['WebsiteMode']>('guided');
  const org = active.find((o) => o.id === orgId) ?? active[0];

  const create = useMutation({
    mutationFn: () => {
      if (!org) throw new Error('no organization');
      return createWebsite(org.id, { type, mode });
    },
    onSuccess: (site) => {
      void qc.invalidateQueries({ queryKey: ['websites'] });
      void qc.invalidateQueries({ queryKey: ['organizations'] });
      toast(t('modal.created'));
      onClose();
      navigate(paths.site(site.id, 'structure'));
    },
  });

  return (
    <Modal open onClose={onClose} labelledBy={titleId}>
      <ModalHeader id={titleId} title={t('modal.title')} sub={t('modal.sub')} onClose={onClose} closeLabel={tc('actions.close')} />
      <div className={s.section}>
        <CapsLabel id={`${titleId}-org`}>{t('modal.organization')}</CapsLabel>
        <Select
          icon="building-2"
          aria-labelledby={`${titleId}-org`}
          value={org?.id ?? ''}
          onChange={(e) => setOrgId(e.target.value)}
          options={active.map((o) => ({
            value: o.id,
            label: t('modal.org_option', { brand: o.brand_name, industry: o.industry ?? tc('dash'), pm: o.pms[0] ? shortName(o.pms[0].full_name) : tc('dash') }),
          }))}
        />
        {org && <FieldHint>{t('modal.org_note', { plan: org.plan?.name ?? tc('dash') })}</FieldHint>}
      </div>
      <div className={`${s.section} ${s.section10}`}>
        <CapsLabel id={`${titleId}-type`}>{t('modal.type')}</CapsLabel>
        <div className={s.options} role="radiogroup" aria-labelledby={`${titleId}-type`}>
          <OptionCard icon="shopping-cart" title={t('modal.type_ecommerce')} text={t('modal.type_ecommerce_text')} selected={type === 'ecommerce'} onSelect={() => setType('ecommerce')} />
          <OptionCard icon="file" title={t('modal.type_landing')} text={t('modal.type_landing_text')} selected={type === 'landing'} onSelect={() => setType('landing')} />
        </div>
      </div>
      <div className={`${s.section} ${s.section10}`}>
        <CapsLabel id={`${titleId}-mode`}>{t('modal.mode')}</CapsLabel>
        <div className={s.options} role="radiogroup" aria-labelledby={`${titleId}-mode`}>
          <OptionCard icon="layers" title={t('modal.mode_guided')} text={t('modal.mode_guided_text')} selected={mode === 'guided'} onSelect={() => setMode('guided')} />
          <OptionCard icon="wand-sparkles" title={t('modal.mode_free')} text={t('modal.mode_free_text')} selected={mode === 'free'} onSelect={() => setMode('free')} />
        </div>
      </div>
      <ModalFooter>
        <Button size={46} variant="secondary" onClick={onClose}>
          {tc('actions.cancel')}
        </Button>
        <Button size={46} trailingIcon="arrow-right" disabled={!org || create.isPending} onClick={() => create.mutate()}>
          {t('modal.submit')}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
